// Eine Zeile im Reiter Messages: Typ-Kreis (zugleich Erledigen-Knopf), Lead und Kontakt, Text (fett, solange ungelesen), Status der
// Sendung, Zeit und Knöpfe. Aufbau und Maße wie die Aufgabenzeilen der Inbox (`.aufgabe`).
import type { SmsZeile as Sms } from "../../api/smsEingang";
import { zeitpunktLang } from "../../werkzeuge/zeit";
import { Icon } from "../Icon";
import { einzeilig, gegenstelle, istUngelesen, nachrichtZeit, planZeit, STATUS_TEXT } from "./nachrichtenLogik";
import "./nachrichten.css";

interface Props {
  sms: Sms;
  fokus: boolean;
  offen: boolean;
  /** gehört dem angemeldeten Nutzer (dann steht kein Name in der Zeile) */
  eigene: boolean;
  an: boolean;
  beschaeftigt: boolean;
  onUmschalten: (bereich: boolean) => void;
  onOeffnen: () => void;
  onErledigt: () => void;
  onLead: () => void;
  onGelesen: () => void;
  onAbbrechen: () => void;
}

export function SmsZeile({ sms: s, fokus, offen, eigene, an, beschaeftigt, onUmschalten, onOeffnen, onErledigt, onLead, onGelesen, onAbbrechen }: Props) {
  const ein = s.richtung === "eingehend";
  const ungelesen = istUngelesen(s);
  const geplant = s.status === "geplant";
  const fehler = s.status === "fehlgeschlagen";
  const kannErledigen = !geplant;
  const erledigt = s.erledigt && (ein || fehler); // gesendete SMS zeigen immer den Pfeil, nicht den Haken
  const art = fehler && !erledigt ? "fehler" : ein ? "ein" : "aus";
  const titel = geplant ? "Geplante SMS" : erledigt ? "Wieder öffnen" : ein ? "Erledigt (e)" : fehler ? "Fehler zur Kenntnis genommen (e)" : "Gesendete SMS";

  return (
    <div
      className={`aufgabe sms-zeile${ungelesen ? " sms-zeile--neu" : ""}${s.erledigt ? " aufgabe--erledigt" : ""}${fokus ? " aufgabe--fokus" : ""}${offen ? " sms-zeile--offen" : ""}`}
      data-sms={s.id}
      aria-expanded={offen}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest("button, a, input, textarea, label")) return;
        onOeffnen();
      }}
    >
      <label
        className="aufgabe__auswahl-feld"
        title="Markieren (x)"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onUmschalten(e.shiftKey);
        }}
      >
        <input
          type="checkbox"
          className="aufgabe__auswahl"
          checked={an}
          aria-label={`${gegenstelle(s)} markieren`}
          onChange={() => undefined}
        />
      </label>
      <button
        type="button"
        className={`aufgabe__haken aufgabe__haken--typ sms-kreis sms-kreis--${art}${erledigt ? " aufgabe__haken--an" : ""}`}
        title={titel}
        aria-label={titel}
        disabled={beschaeftigt || !kannErledigen || (!ein && !fehler && !erledigt)}
        onClick={onErledigt}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          {erledigt ? <path d="M5 12.5l4.5 4.5L19 7" /> : geplant ? <path d="M12 21a9 9 0 100-18 9 9 0 000 18zM12 7v5l3 2" /> : fehler ? <path d="M12 7v6M12 17h.01" /> : ein ? <path d="M4 5h16v11H9l-5 4z" /> : <path d="M7 17L17 7M9 7h8v8" />}
        </svg>
      </button>
      <div className="sms-zeile__inhalt">
        <span className="sms-zeile__lead">
          {s.lead_id ? (
            <a
              href={`/lead/${encodeURIComponent(s.lead_id)}`}
              className="aufgabe__leadname"
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                e.preventDefault();
                onLead();
              }}
            >
              {s.lead_name ?? "Lead"}
            </a>
          ) : (
            <span className="aufgabe__nummer aufgabe__nummer--leise" title="Die Nummer gehört zu keinem Lead">
              {s.entfernte_anzeige ?? s.entfernte_nummer ?? "Unbekannt"}
            </span>
          )}
          {s.contact_name && s.contact_name !== s.lead_name ? <span className="aufgabe__kontakt"> · {s.contact_name}</span> : null}
          {!s.lead_id ? <span className="status-pille status-pille--klein kein-lead-pille">Kein Lead</span> : null}
        </span>
        <span className="sms-zeile__text" title={einzeilig(s.text)}>
          {!ein ? <span className="sms-zeile__du">{geplant ? "Geplant: " : "Du: "}</span> : null}
          {einzeilig(s.text) || "(leer)"}
        </span>
        <span className="sms-zeile__status">
          {!ein ? (
            <span className={`sms-status sms-status--${s.status}${s.ueberfaellig ? " sms-status--ueberfaellig" : ""}`} title={fehler ? s.fehler_text ?? undefined : undefined}>
              {geplant ? (s.ueberfaellig ? "Überfällig" : STATUS_TEXT.geplant) : STATUS_TEXT[s.status]}
            </span>
          ) : s.nutzer_name && !s.erledigt && !eigene ? (
            <span className="leise sms-zeile__person">{s.nutzer_name}</span>
          ) : null}
        </span>
      </div>
      <span className={`aufgabe__datum${s.ueberfaellig ? " aufgabe__datum--ueberfaellig" : ""}`} title={zeitpunktLang(geplant ? s.geplant_at : s.date_created)}>
        {geplant ? planZeit(s.geplant_at) : nachrichtZeit(s.date_created)}
      </span>
      <span className="aufgabe__aktionen sms-zeile__aktionen">
        {ein && s.lead_id ? (
          <button type="button" className="symbolknopf" title="Antworten (r)" aria-label="Antworten" onClick={onOeffnen}>
            <Icon name="sms" groesse={14} />
          </button>
        ) : null}
        {ein ? (
          <button type="button" className="symbolknopf" title={ungelesen ? "Als gelesen markieren" : "Als ungelesen markieren (u)"} aria-label={ungelesen ? "Als gelesen markieren" : "Als ungelesen markieren"} onClick={onGelesen}>
            <Icon name={ungelesen ? "haken" : "mail"} groesse={14} />
          </button>
        ) : null}
        {geplant && s.kann_aufheben ? (
          <button type="button" className="knopf knopf--klein knopf--leise" disabled={beschaeftigt} onClick={onAbbrechen} title="Planung aufheben">
            Aufheben
          </button>
        ) : null}
      </span>
    </div>
  );
}
